import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PlanForm from './PlanForm';
import Icons from '../../svgs/icons';

const PlanManagement = ({
  title,
  plans,
  onChange,
  disabled = false,
  placeholder = 'No plans added yet'
}) => {
  const [expandedPlanIndex, setExpandedPlanIndex] = useState(null);

  const handleAddPlan = () => {
    const newPlan = {
      planName: '',
      description: '',
      points: [''],
      contact: '',
      info: ''
    };

    const updatedPlans = [...plans, newPlan];
    onChange(updatedPlans);

    // Auto-expand the newly added plan
    setExpandedPlanIndex(updatedPlans.length - 1);
  };

  const handlePlanChange = (index, field, value) => {
    const updatedPlans = [...plans];
    updatedPlans[index] = {
      ...updatedPlans[index],
      [field]: value
    };
    onChange(updatedPlans);
  };

  const handleRemovePlan = (index) => {
    const updatedPlans = plans.filter((_, i) => i !== index);
    onChange(updatedPlans);

    // Reset expanded state if removing the expanded plan
    if (expandedPlanIndex === index) {
      setExpandedPlanIndex(null);
    } else if (expandedPlanIndex !== null && expandedPlanIndex > index) {
      setExpandedPlanIndex(expandedPlanIndex - 1);
    }
  };

  const handleToggleExpand = (index) => {
    setExpandedPlanIndex(expandedPlanIndex === index ? null : index);
  };

  const getPlanSummary = () => {
    const totalPlans = plans.length;
    const completedPlans = plans.filter(
      (plan) => plan.planName?.trim() && plan.description?.trim()
    ).length;

    return { totalPlans, completedPlans };
  };

  const { totalPlans, completedPlans } = getPlanSummary();

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
          {totalPlans > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {completedPlans} of {totalPlans} plans completed
            </p>
          )}
        </div>

        {!disabled && (
          <button
            onClick={handleAddPlan}
            className="btn-primary"
          >
            <Icons.plusCircle className="w-4 h-4" />
            Add Plan
          </button>
        )}
      </div>

      {/* Plans List */}
      <div className="space-y-4">
        {plans.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icons.documentText className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{placeholder}</p>
            {!disabled && (
              <button
                onClick={handleAddPlan}
                className="mt-4 text-shoorah-primary hover:text-shoorah-secondary text-sm font-medium"
              >
                Add your first plan
              </button>
            )}
          </div>
        ) : (
          plans.map((plan, index) => (
            <PlanForm
              key={index}
              plan={plan}
              index={index}
              onChange={handlePlanChange}
              onRemove={handleRemovePlan}
              disabled={disabled}
              isExpanded={expandedPlanIndex === index}
              onToggleExpand={handleToggleExpand}
            />
          ))
        )}
      </div>

      {/* Summary Footer */}
      {!disabled && totalPlans > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Plans: {totalPlans}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Progress: {completedPlans}/{totalPlans}
              </div>
              <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-shoorah-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: totalPlans > 0 ? `${(completedPlans / totalPlans) * 100}%` : '0%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PlanManagement.propTypes = {
  title: PropTypes.string.isRequired,
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      planName: PropTypes.string,
      description: PropTypes.string,
      points: PropTypes.arrayOf(PropTypes.string),
      contact: PropTypes.string,
      info: PropTypes.string
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
};

PlanManagement.defaultProps = {
  disabled: false,
  placeholder: 'No plans added yet'
};

export default PlanManagement;
